import { diagnostics } from '@blend/ui-common';
import { Query } from 'history';
import * as _ from 'lodash';

type OptionalQueryParam =
  | 'referrerId'
  | 'referringRealtor'
  | 'app_source'
  | 'applicationTemplateId'
  | 'displayLink'
  | 'loanType'
  | 'comid'
  | 'shoppedProductId'
  | 'isFollowUpOnly'
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_term'
  | 'utm_content'
  | 'signupUrl';

type ParamValue = string | string[] | Record<string, string>;
export interface CreationParams {
  [OptionalQueryParam: string]: ParamValue;
}

export const allMarketingParams: OptionalQueryParam[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/**
 * When URLs are structured like so: http://site.com?param=1&param=2&param=3
 * Most routing libraries denormalize them to { param: [1, 2, 3] }, which is what happens here.
 * We don't currently support any scenarios where we rely on this, but our customers sometimes
 * mess up URLs and duplicate parameters. This function helps us get reasonably-structured
 * parameter objects, as well as alerting when we view those to be malformed.
 */
export function deduplicateParameters(signupParams: CreationParams) {
  _.each(signupParams, (value, key) => {
    if (_.isArray(value)) {
      const dedupedValues = _.uniq(value);
      if (dedupedValues.length > 1) {
        diagnostics.error(new Error(`Multiple unique values for query param ${key}`));
        diagnostics.warn(`There are multiple values sent for query parameter ${key}. Using the first value.`);
      }

      signupParams[key] = _.first(dedupedValues)!;
    }
  });

  return signupParams;
}

export function getOptionalCreationParams(
  currentStateQuery: Query,
  optionalParams: OptionalQueryParam[],
): CreationParams {
  const optionalParamValues = _.pick<any, any>(currentStateQuery, optionalParams);

  // Clean falsy/undefined values from the signupParams object.
  const cleanSignupParams = deduplicateParameters(_.pickBy(optionalParamValues));

  // We use displayLink as the user-facing param, but we send applicationTemplateDisplayLink to the backend for
  // naming clarity.
  if (cleanSignupParams.displayLink) {
    cleanSignupParams.applicationTemplateDisplayLink = cleanSignupParams.displayLink;
    delete cleanSignupParams.displayLink;
  }

  // We use comId as the user-facing param (client-specific reasons -- a specific customer had built integrations and
  // deployed lots of LO pages before we'd built this), but communityId as the internal param.
  if (cleanSignupParams.comid) {
    cleanSignupParams.communityId = cleanSignupParams.comid;
    delete cleanSignupParams.comid;
  }

  // consolidate utm params
  const marketingParams = _.intersection(_.keys(cleanSignupParams), allMarketingParams);
  if (marketingParams.length) {
    cleanSignupParams.marketing = {};
    _.each(marketingParams, param => {
      cleanSignupParams.marketing[param] = cleanSignupParams[param];
      delete cleanSignupParams[param];
    });
  }

  return cleanSignupParams;
}

export function getMessageQueryParam(currentStateQuery: Query) {
  return _.get(currentStateQuery, 'message');
}
